import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import {
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  AreaChart, Area, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ComposedChart, Bar
} from 'recharts';
import { FiTrendingUp, FiAlertTriangle, FiCheckCircle, FiZap, FiTarget, FiAward, FiCpu, FiShield } from 'react-icons/fi';

/* ══ CONQUER Cyan Theme ══ */
const THEME = {
  primary: '#00B4D8',
  secondary: '#48CAE4',
  accent: '#90E0EF',
  success: '#059669',
  warning: '#D97706',
  danger: '#DC2626',
  dark: '#0A1628',
  slate: '#0096C7',
  muted: '#48CAE4',
};

const CHANNEL_COLORS = ['#7C3AED', '#8B5CF6', '#A78BFA', '#059669', '#D97706', '#6366F1'];

const TYPE_LABELS = {
  whatsapp: 'WhatsApp', whatsapp_image: 'WA Image', whatsapp_video: 'WA Video',
  whatsapp_document: 'WA Doc', sms: 'SMS', email: 'Email',
  rcs: 'RCS', newsletter: 'Newsletter', push_notification: 'Push',
  voice_call: 'Voice', status_messaging: 'Status', custom: 'Custom'
};

/* ── Crystal Tooltip ── */
const CrystalTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(16px)',
      border: '2px solid #E0E7FF', borderRadius: 14,
      padding: '14px 18px', boxShadow: '0 12px 40px rgba(124,58,237,0.15)',
    }}>
      <p style={{ color: THEME.dark, fontSize: 13, fontWeight: 700, marginBottom: 8 }}>{label}</p>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <span style={{ width: 10, height: 10, borderRadius: 3, background: p.color || p.fill }} />
          <span style={{ color: THEME.muted, fontSize: 12 }}>{p.name}:</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: THEME.dark }}>{p.value?.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};

/* ── Insight Card ── */
const InsightCard = ({ icon, title, value, color, tip, trend }) => (
  <div style={{
    background: '#fff', borderRadius: 16, padding: 22, position: 'relative',
    border: `1px solid ${color}25`, boxShadow: `0 4px 20px ${color}10`,
    overflow: 'hidden',
  }}>
    <div style={{
      position: 'absolute', top: 0, right: 0, width: 100, height: 100,
      background: `radial-gradient(circle at top right, ${color}15 0%, transparent 70%)`,
    }} />
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
      <div style={{
        width: 46, height: 46, borderRadius: 12,
        background: `linear-gradient(135deg, ${color}, ${color}BB)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: `0 6px 16px ${color}30`,
      }}>
        {React.cloneElement(icon, { size: 22, color: '#fff' })}
      </div>
      {trend && (
        <div style={{
          padding: '5px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
          background: trend.up ? `${THEME.success}15` : `${THEME.danger}15`,
          color: trend.up ? THEME.success : THEME.danger,
        }}>
          {trend.up ? '↑' : '↓'} {trend.label}
        </div>
      )}
    </div>
    <div style={{ fontSize: 11, fontWeight: 600, color: THEME.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{title}</div>
    <div style={{ fontSize: 30, fontWeight: 800, color, lineHeight: 1.1, marginBottom: 10 }}>{value}</div>
    <p style={{ fontSize: 12, color: '#64748b', margin: 0, lineHeight: 1.5 }}>{tip}</p>
  </div>
);

/* ── Section Header ── */
const SectionHeader = ({ icon, title, subtitle }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
    <div style={{
      width: 40, height: 40, borderRadius: 10,
      background: `linear-gradient(135deg, ${THEME.primary}12, ${THEME.accent}08)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center', color: THEME.primary,
    }}>
      {icon}
    </div>
    <div>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: THEME.dark, margin: 0 }}>{title}</h3>
      {subtitle && <p style={{ fontSize: 12, color: THEME.muted, margin: 0 }}>{subtitle}</p>}
    </div>
  </div>
);

export default function AdminInsights() {
  const [data, setData] = useState(null);
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/admin/dashboard'),
      api.get('/admin/insights-tips'),
    ]).then(([dashRes, tipsRes]) => {
      setData(dashRes.data);
      setTips(tipsRes.data?.tips || []);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ width: 48, height: 48, border: '4px solid #E0E7FF', borderTopColor: THEME.primary, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
  if (!data) return null;

  const { stats = {}, channelBreakdown = [] } = data;
  const totalMessages = (stats.totalSent || 0) + (stats.totalFailed || 0);
  const deliveryRate = totalMessages > 0 ? ((stats.totalSent / totalMessages) * 100).toFixed(1) : 0;
  const failRate = totalMessages > 0 ? ((stats.totalFailed / totalMessages) * 100).toFixed(1) : 0;

  /* Insights */
  const insights = [
    {
      icon: <FiTrendingUp />, title: 'Delivery Rate', value: `${deliveryRate}%`,
      color: deliveryRate >= 90 ? THEME.success : deliveryRate >= 70 ? THEME.warning : THEME.danger,
      tip: deliveryRate >= 90 ? 'Outstanding performance! Keep it up.' : deliveryRate >= 70 ? 'Good, but there\'s room for improvement.' : 'Attention needed! Review your data quality.',
      trend: { up: deliveryRate >= 70, label: deliveryRate >= 70 ? 'Healthy' : 'Low' },
    },
    {
      icon: <FiAlertTriangle />, title: 'Failure Rate', value: `${failRate}%`,
      color: failRate <= 5 ? THEME.success : failRate <= 15 ? THEME.warning : THEME.danger,
      tip: failRate <= 5 ? 'Excellent! Minimal failures detected.' : failRate <= 15 ? 'Moderate failures. Check data validity.' : 'High failure rate. Audit master data.',
      trend: { up: failRate <= 10, label: failRate <= 10 ? 'Low' : 'High' },
    },
    {
      icon: <FiTarget />, title: 'Campaign Activity',
      value: `${stats.activeCampaigns || 0}/${stats.totalCampaigns || 0}`,
      color: THEME.primary,
      tip: stats.activeCampaigns > 0 ? `${stats.activeCampaigns} campaigns currently running.` : 'No active campaigns right now.',
    },
    {
      icon: <FiAward />, title: 'Data Pool',
      value: (stats.totalMasterData || 0).toLocaleString(),
      color: THEME.slate,
      tip: (stats.totalMasterData || 0) >= 1000 ? 'Large dataset ready for campaigns.' : 'Import more data for broader reach.',
    },
  ];

  /* Channel Performance for Area Chart */
  const channelPerf = (channelBreakdown || []).map((ch, i) => ({
    channel: TYPE_LABELS[ch._id] || ch._id,
    Sent: ch.totalSent || 0,
    Failed: ch.totalFailed || 0,
    color: CHANNEL_COLORS[i % CHANNEL_COLORS.length],
  })).filter(c => c.Sent + c.Failed > 0);

  /* Radar Data */
  const radarData = [
    { metric: 'Delivery', value: Math.min(100, parseFloat(deliveryRate) || 0), fullMark: 100 },
    { metric: 'Campaigns', value: Math.min(100, (stats.totalCampaigns || 0) * 2), fullMark: 100 },
    { metric: 'Users', value: Math.min(100, (stats.totalUsers || 0) * 10), fullMark: 100 },
    { metric: 'Data', value: Math.min(100, ((stats.totalMasterData || 0) / 100)), fullMark: 100 },
    { metric: 'Active', value: Math.min(100, (stats.activeCampaigns || 0) * 10), fullMark: 100 },
  ];

  return (
    <div style={{ padding: '0 4px' }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, color: THEME.dark, margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          <FiCpu size={28} style={{ color: THEME.primary }} />
          Intelligence Hub
        </h1>
        <p style={{ fontSize: 14, color: THEME.muted, marginTop: 6 }}>
          Performance insights & AI-powered recommendations
        </p>
      </div>

      {/* Insight Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, marginBottom: 28 }}>
        {insights.map((ins, i) => <InsightCard key={i} {...ins} />)}
      </div>

      {/* Charts Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.8fr 1fr', gap: 24, marginBottom: 28 }}>
        {/* Channel Performance Composed Chart */}
        <div style={{ background: '#fff', borderRadius: 18, padding: 24, border: `1px solid ${THEME.accent}30`, boxShadow: '0 4px 24px rgba(124,58,237,0.08)' }}>
          <SectionHeader icon={<FiShield size={18} />} title="Channel Performance" subtitle="Sent vs Failed by channel" />
          {channelPerf.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={channelPerf} margin={{ top: 10, right: 20, bottom: 0, left: -10 }}>
                <defs>
                  <linearGradient id="insAreaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={THEME.primary} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={THEME.primary} stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="4 4" stroke="#E0E7FF" vertical={false} />
                <XAxis dataKey="channel" tick={{ fill: THEME.muted, fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: THEME.muted, fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<CrystalTooltip />} />
                <Legend formatter={(v) => <span style={{ color: THEME.dark, fontWeight: 600 }}>{v}</span>} />
                <Area type="monotone" dataKey="Sent" fill="url(#insAreaGrad)" stroke={THEME.primary} strokeWidth={2} />
                <Bar dataKey="Failed" fill={THEME.danger} barSize={24} radius={[6, 6, 0, 0]} />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: THEME.muted }}>
              <div style={{ textAlign: 'center' }}>
                <FiShield size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
                <div style={{ fontWeight: 600 }}>No channel data yet</div>
              </div>
            </div>
          )}
        </div>

        {/* System Health Radar */}
        <div style={{ background: '#fff', borderRadius: 18, padding: 24, border: `1px solid ${THEME.accent}30`, boxShadow: '0 4px 24px rgba(124,58,237,0.08)' }}>
          <SectionHeader icon={<FiCpu size={18} />} title="System Health" subtitle="Overall performance radar" />
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart data={radarData} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
              <PolarGrid stroke="#E0E7FF" />
              <PolarAngleAxis dataKey="metric" tick={{ fill: THEME.muted, fontSize: 11, fontWeight: 600 }} />
              <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} />
              <Radar name="Health" dataKey="value" stroke={THEME.primary} fill={THEME.primary} fillOpacity={0.35} strokeWidth={2} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Performance Tips */}
      <div style={{ background: '#fff', borderRadius: 18, padding: 24, border: `1px solid ${THEME.accent}30`, boxShadow: '0 4px 24px rgba(124,58,237,0.08)' }}>
        <SectionHeader icon={<FiZap size={18} />} title="AI Recommendations" subtitle="Smart tips to boost performance" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          {tips.slice(0, 6).map((tip, i) => (
            <div key={i} style={{
              display: 'flex', gap: 14, padding: '18px 20px', borderRadius: 14,
              background: `linear-gradient(135deg, ${THEME.primary}05, ${THEME.accent}08)`,
              border: '1px solid #E0E7FF', transition: 'all 0.2s',
            }}>
              <span style={{ fontSize: 28, lineHeight: 1 }}>{tip.icon || '💡'}</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: THEME.dark, marginBottom: 4 }}>{tip.title}</div>
                <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>{tip.desc}</div>
              </div>
            </div>
          ))}
          {tips.length === 0 && (
            <>
              <div style={{ display: 'flex', gap: 14, padding: '18px 20px', borderRadius: 14, background: `${THEME.primary}05`, border: '1px solid #E0E7FF' }}>
                <span style={{ fontSize: 28 }}>🚀</span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: THEME.dark, marginBottom: 4 }}>No Sends Today</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>Launch a campaign to engage users.</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 14, padding: '18px 20px', borderRadius: 14, background: `${THEME.success}05`, border: '1px solid #E0E7FF' }}>
                <span style={{ fontSize: 28 }}>📊</span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: THEME.dark, marginBottom: 4 }}>Expand Data</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>Import more contacts for wider reach.</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 14, padding: '18px 20px', borderRadius: 14, background: `${THEME.warning}05`, border: '1px solid #E0E7FF' }}>
                <span style={{ fontSize: 28 }}>⏰</span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: THEME.dark, marginBottom: 4 }}>Timing Matters</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>Schedule sends during peak hours.</div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
